/* @jsx h */
import { defineComponent, h } from "vue";

// Person/role colours: picked from the theme so they work in light and dark.
export const PERSON_COLORS = ["primary", "secondary", "accent", "info", "success", "warning", "error", "neutral"] as const;

export const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join("") || "?";

/** A stable colour for someone without one set. */
export const colorFor = (key: string | number, color?: string | null) => {
  if (color && (PERSON_COLORS as readonly string[]).includes(color)) return color;
  const s = String(key);
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  return PERSON_COLORS[hash % (PERSON_COLORS.length - 1)];
};

// Tailwind needs whole class names in the source.
const BG: Record<string, string> = {
  primary: "bg-primary text-primary-content",
  secondary: "bg-secondary text-secondary-content",
  accent: "bg-accent text-accent-content",
  info: "bg-info text-info-content",
  success: "bg-success text-success-content",
  warning: "bg-warning text-warning-content",
  error: "bg-error text-error-content",
  neutral: "bg-neutral text-neutral-content",
};

export const colorClass = (color: string) => BG[color] ?? BG.neutral;

type Props = { name: string; color?: string | null; id?: number | string; size?: "xs" | "sm" | "md" | "lg"; dim?: boolean };

const SIZE = { xs: "h-6 w-6 text-[10px]", sm: "h-8 w-8 text-xs", md: "h-10 w-10 text-sm", lg: "h-14 w-14 text-lg" };

export const Avatar = defineComponent<Props>({
  name: "Avatar",
  props: ["name", "color", "id", "size", "dim"],
  setup(props) {
    return () => (
      <span
        class={[
          "inline-flex shrink-0 items-center justify-center rounded-full font-semibold",
          SIZE[props.size ?? "sm"],
          colorClass(colorFor(props.id ?? props.name, props.color)),
          props.dim ? "opacity-40" : "",
        ].join(" ")}
        aria-hidden="true"
      >
        {initials(props.name)}
      </span>
    );
  },
});
